import { CreditCard, QrCode, Users, Search, ArrowRight, Check, BadgeCheck } from "lucide-react";

const WHAT_SPONSORS_DO = [
  { icon: CreditCard, text: "Order Sharing Cards customized with their organization's logo" },
  { icon: QrCode, text: "Use a branded QR code connected to their organization" },
  { icon: Users, text: "Make the cards available to members, visitors, customers, employees, students, or the community" },
  { icon: Search, text: "Help people quickly find relevant Proverbs and biblical encouragement when they need it" },
];

export default function Sponsor() {
  return (
    <div data-testid="sponsor-page">
      {/* Hero */}
      <section className="relative overflow-hidden paper-texture border-b border-[#EFE7D6]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-12 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] font-semibold mb-4">Sponsor Program</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-[#0F172A]">
            Help Share Biblical Wisdom With More People
          </h1>
          <p className="mt-5 text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
            The I Need a Proverb Sponsor Program gives churches, ministries, Christian schools, businesses, and organizations a simple way to share practical biblical wisdom with the people they serve.
          </p>
        </div>
      </section>

      {/* What sponsors can do */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
        <h2 className="font-serif-display text-2xl sm:text-3xl text-[#0F172A] text-center">What sponsors can do</h2>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {WHAT_SPONSORS_DO.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-start gap-4 rounded-2xl border border-[#E7DFC9] bg-white px-6 py-5">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#EAF1EA] shrink-0">
                <Icon className="w-5 h-5 text-[#2C4C3B]" strokeWidth={1.8} />
              </span>
              <p className="text-[#475569] leading-relaxed text-sm pt-1.5">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Free-to-use reassurance */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-6">
        <div className="rounded-2xl bg-[#EAF1EA] border border-[#CFE0CF] px-6 sm:px-8 py-6 flex items-start gap-4">
          <span className="grid place-items-center w-10 h-10 rounded-full bg-white shrink-0"><Check className="w-5 h-5 text-[#2C4C3B]" strokeWidth={2.5} /></span>
          <p className="text-[#2C4C3B] leading-relaxed text-sm sm:text-base">
            <strong>I Need a Proverb remains free to use.</strong> Sponsorship is about helping distribute and share the resource with more people — never about charging anyone to access Proverbs and biblical wisdom.
          </p>
        </div>
      </section>

      {/* Primary CTA */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-10 text-center">
        <h3 className="font-serif-display text-2xl sm:text-3xl text-[#0F172A]">Interested in becoming a sponsor?</h3>
        <p className="mt-3 text-[#475569] max-w-xl mx-auto leading-relaxed">
          We'd love to hear about your church, ministry, school, business, or organization and how you'd like to share biblical wisdom.
        </p>
        <a data-testid="sponsor-contact-btn" target="_top" href="mailto:wisdom@ineedaproverb.com?subject=Sponsor%20Program%20Question"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2C4C3B] text-white px-7 py-3.5 font-semibold hover:bg-[#24402F] transition-colors">
          Ask About Becoming a Sponsor <ArrowRight className="w-4 h-4" />
        </a>
        <p className="mt-5 text-sm text-[#475569]">
          Questions? <a data-testid="sponsor-email-link" target="_top" href="mailto:wisdom@ineedaproverb.com?subject=Sponsor%20Program%20Question" className="font-semibold text-[#2C4C3B] underline underline-offset-2 hover:text-[#1E3629]">Email Us</a> at <span className="font-semibold text-[#0F172A]">wisdom@ineedaproverb.com</span>
        </p>
      </section>

      {/* Secondary callout: branded cards */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-20">
        <div data-testid="sponsor-branded-callout" className="rounded-2xl border border-[#E7DFC9] bg-[#FBF7EE] px-6 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#EAF1EA] shrink-0"><BadgeCheck className="w-5 h-5 text-[#2C4C3B]" strokeWidth={1.8} /></span>
          <div className="flex-1">
            <p className="font-serif-display text-lg text-[#0F172A]">Want Sharing Cards with your organization's logo and branded QR code?</p>
            <p className="mt-1 text-sm text-[#475569] leading-relaxed">We can help create a customized card order for your organization.</p>
          </div>
          <a data-testid="sponsor-branded-btn" target="_top" href="mailto:wisdom@ineedaproverb.com?subject=Custom%20Branded%20Sharing%20Cards&body=Hi%2C%20we%27re%20interested%20in%20Sharing%20Cards%20with%20our%20organization%27s%20logo%20and%20a%20branded%20QR%20code.%20Please%20contact%20us%20with%20options%20and%20pricing."
            className="shrink-0 rounded-full border border-[#2C4C3B] text-[#2C4C3B] px-5 py-2.5 text-sm font-semibold hover:bg-[#F1F5F1] transition-colors whitespace-nowrap">
            Ask about custom cards
          </a>
        </div>
      </section>
    </div>
  );
}
